import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import React, { useContext } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import StorageBrazeCard from 'src/cms-components/StorageBrazeCard/StorageBrazeCard';
import {
  getBrazeBanners,
  getBrazeListingCards,
  useBrazeResults,
} from 'src/hooks/useBraze/useBraze';
import { formatSku } from '../../utils/helpers';
import OfferCard from '../offer_card/OfferCard';
import OfferCardSkeleton from '../offer_card/OfferCardSkeleton';
import NoResults from './NoResults';
import { SearchListingContext } from './SearchListingContext';

interface Props {
  serverData?: DrupalSearch;
  brazeCardSlots?: IStorageBrazeCard[];
}

const OfferSearchResults: React.FC<Props> = ({ serverData, brazeCardSlots }) => {
  const context = useContext(SearchListingContext);

  if (!context) {
    throw new Error(
      'Child components of SearchListing cannot be rendered outside the SearchListing component!',
    );
  }

  const { data, loaded } = context || {};
  const search_results = serverData?.search_results || data?.search_results;

  const banners = getBrazeBanners(brazeCardSlots);
  const resultsWithBrazeCards = useBrazeResults({ search_results, brazeCardSlots });
  const allBrazeCards = context.brazeCards;
  const brazeListingCards = getBrazeListingCards(allBrazeCards);

  if (!loaded) {
    return (
      <Row className="mb-5 gy-3 gy-md-4 gx-2 gx-md-3">
        {[...Array(12)].map((_, i) => {
          return (
            <Col key={`loading-card-${i}`} xs={6} md={4} className="d-flex">
              <OfferCardSkeleton />
            </Col>
          );
        })}
      </Row>
    );
  }

  if (!resultsWithBrazeCards) {
    return <NoResults />;
  }

  const renderListingGrid = (
    <Row id="search-results" className="mb-5 gy-3 gy-md-4 gx-2 gx-md-3">
      {Object.values(resultsWithBrazeCards).map(node => {
        const isBrazeCard = node.field_campaign_type?.length;
        const sku = node.upc ? formatSku(node.upc) : '';

        if (isBrazeCard) {
          const brazeCardIndex = resultsWithBrazeCards
            .filter(result => result?.field_campaign_type)
            .findIndex(card => card.id === node.id);
          return (
            <Col key={node.nid} xs={12} md={4} className="min-[350px]:pds-w-full pds-flex">
              <StorageBrazeCard
                defaultCard={{
                  ...node,
                }}
                brazeCard={brazeListingCards[brazeCardIndex] ?? undefined}
              />
            </Col>
          );
        }

        return (
          <Col key={node.nid} xs={6} md={4} className="d-flex">
            <OfferCard
              image={{
                images: {
                  fallback: {
                    src: node.field_media_image ?? '',
                  },
                },
                layout: 'constrained',
                width: 800,
                height: 800,
              }}
              brand={node.field_brand}
              link={node.url}
              title={node.title}
              sku={sku}
              onClick={() => {
                sendIt({
                  event: 'module_interaction',
                  eventParams: {
                    component_id: node.title,
                    component_type: 'product_card',
                    content_name: 'offer_card: offers',
                  },
                });
              }}
            />
          </Col>
        );
      })}
    </Row>
  );

  if (banners?.length) {
    const bannersMapping = (banner: IStorageBrazeCard) => {
      return (
        <Row
          key={banner.drupal_internal__id}
          id="braze-card-banner"
          className="mb-5 gy-3 gy-md-4 gx-2 gx-md-3"
        >
          <Col xs={12} className="min-[350px]:pds-w-full pds-flex">
            <StorageBrazeCard
              // need to check with andrew if default card is the intended prop instead of braze card
              defaultCard={{
                ...banner,
                title: banner.name,
              }}
            />
          </Col>
        </Row>
      );
    };
    const mappedBanners =
      banners.length > 2 ? banners.slice(2).map(bannersMapping) : banners.map(bannersMapping);

    if (mappedBanners?.length > 1) {
      mappedBanners.splice(1, 0, renderListingGrid);
    } else {
      mappedBanners.push(renderListingGrid);
    }

    return mappedBanners;
  }

  return renderListingGrid;
};

OfferSearchResults.displayName = 'SearchListing.OfferSearchResults';

export default OfferSearchResults;
